import { z } from "zod"
import { STORE_CODES } from "./stores"

/** Consumer reads cover all known sources; Magento events keep their PIM-only schema. */
export const nutritionConsumerImageSchema = z
  .object({
    url: z.string().url(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    contentSource: z.enum(["pim", "llm_extracted", "magento_attrs"]),
  })
  .strict()
  .superRefine((image, context) => {
    const url = new URL(image.url)
    if (
      url.protocol !== "https:" ||
      url.search ||
      url.hash ||
      !url.pathname.includes(image.sha256)
    ) {
      context.addIssue({
        code: "custom",
        message: "image URL must identify immutable encoded bytes",
      })
    }
  })
export type NutritionConsumerImage = z.infer<typeof nutritionConsumerImageSchema>

export const NUTRITION_IMAGE_STATE = {
  current: "current",
  stale: "stale",
  missing: "missing",
  withdrawn: "withdrawn",
  excluded: "excluded",
  unavailable: "unavailable",
  unknown: "unknown",
} as const

export const nutritionImageQuerySchema = z
  .object({
    store: z.enum(STORE_CODES),
    skus: z
      .array(
        z
          .string()
          .min(1)
          .max(255)
          .regex(/^[^\u0000-\u001f]+$/)
      )
      .min(1)
      .max(20)
      .refine((skus) => new Set(skus).size === skus.length, "duplicate SKUs are not allowed"),
  })
  .strict()
export type NutritionImageQuery = z.infer<typeof nutritionImageQuerySchema>

const sourceSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("verified"),
    checkedAt: z.string().datetime(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
  }),
  z.object({ state: z.literal("unknown"), checkedAt: z.null(), contentHash: z.null() }),
  z.object({
    state: z.literal("persisted"),
    checkedAt: z.string().datetime(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    extractedAt: z.string().datetime(),
    upstreamVerification: z.literal("not_checked"),
  }),
])
export const nutritionImageAttributionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("direct_pim"), productId: z.number().int().positive() }),
  z.object({
    kind: z.literal("direct_source"),
    productId: z.number().int().positive(),
    sourceStore: z.enum(STORE_CODES),
    attributionMode: z.enum(["exact_label_match", "shared_table", "direct", "llm_matched"]),
  }),
  z.object({
    kind: z.literal("same_flavour_sibling"),
    targetProductId: z.number().int().positive(),
    donor: z.object({
      sku: z.string().min(1),
      productId: z.number().int().positive(),
      store: z.enum(STORE_CODES),
      locale: z.string().min(1),
    }),
    selection: z.object({
      fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
      policyVersion: z.string().min(1),
      family: z.string().min(1),
      flavor: z.string().min(1),
    }),
    ownSource: z.object({
      state: z.literal("absent"),
      checkedAt: z.string().datetime(),
      descriptionHash: z.string().regex(/^[a-f0-9]{64}$/),
    }),
  }),
])

const imageSchema = nutritionConsumerImageSchema.safeExtend({
  inputFingerprint: z.string().min(1),
  createdAt: z.string().datetime(),
})
const evidence = {
  sku: z.string().min(1).max(255),
  store: z.enum(STORE_CODES),
  locale: z.string().min(1),
  kind: z.literal("nutrition"),
  imageRevision: z.number().int().positive().max(Number.MAX_SAFE_INTEGER).nullable(),
  classifiedAt: z.string().datetime(),
  source: sourceSchema,
  // Publication provenance identifies bytes; this read does not download GCS objects.
  objectAvailability: z.literal("not_checked"),
}
export const nutritionImageResultSchema = z
  .discriminatedUnion("state", [
    z.object({
      ...evidence,
      state: z.literal(NUTRITION_IMAGE_STATE.current),
      imageRevision: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
      source: z.union([sourceSchema.options[0], sourceSchema.options[2]]),
      image: imageSchema,
      attribution: nutritionImageAttributionSchema,
      reason: z.null(),
    }),
    z.object({
      ...evidence,
      state: z.enum([
        NUTRITION_IMAGE_STATE.stale,
        NUTRITION_IMAGE_STATE.missing,
        NUTRITION_IMAGE_STATE.withdrawn,
        NUTRITION_IMAGE_STATE.excluded,
        NUTRITION_IMAGE_STATE.unavailable,
        NUTRITION_IMAGE_STATE.unknown,
      ]),
      image: z.null(),
      attribution: z.null(),
      reason: z.string().min(1).max(128),
    }),
  ])
  .superRefine((item, context) => {
    if (item.state !== "current") {
      return
    }
    const consistent =
      item.image.contentSource === "pim"
        ? item.source.state === "verified" && item.attribution.kind !== "direct_source"
        : item.source.state === "persisted" && item.attribution.kind === "direct_source"
    if (!consistent) {
      context.addIssue({ code: "custom", message: "image source and attribution must agree" })
    }
  })
export type NutritionImageResult = z.infer<typeof nutritionImageResultSchema>
export const nutritionImageResponseSchema = z.object({
  schemaVersion: z.literal(3),
  items: z.array(nutritionImageResultSchema).min(1).max(20),
})
export type NutritionImageResponse = z.infer<typeof nutritionImageResponseSchema>
